import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddPointFlow from "../AddPoint/AddPointFlow";
import ViewStatementModal from "../ViewStatement/ViewStatementModal";
import WithdrawPanel from "./WithdrawPanel";
import { getAvailableBalance, getRangeList } from "Store/Reducer/common.slice";
import { getMarginSummary } from "Store/Reducer/margin.slice";
import AccountMarginBar from "../../account/AccountMarginBar";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "add", label: "Add funds" },
  { id: "withdraw", label: "Withdraw" },
  { id: "statement", label: "Statement" },
];

export default function WalletHubModal({
  onClose,
  user,
  displayName,
  ledgerLabel,
  ledgerColor,
  marginSummary: marginSummaryProp,
  marginSummaryLoading: marginLoadingProp,
  onAddSuccess,
  onRefreshBalance,
  darkTheme = false,
}) {
  const dispatch = useDispatch();
  const { availableBalanceLoading } = useSelector((s) => s.common);
  const { marginSummary: marginFromStore, marginSummaryLoading } = useSelector(
    (s) => s.margin || {}
  );
  const summary = marginSummaryProp ?? marginFromStore;
  const summaryLoading = marginLoadingProp ?? marginSummaryLoading;
  const [tab, setTab] = useState("overview");

  const themeClass = darkTheme
    ? "platform-menu-modal-overlay--dark"
    : "platform-menu-modal-overlay--light";

  useEffect(() => {
    if (user?.userId) {
      dispatch(getAvailableBalance());
      dispatch(getMarginSummary());
    }
  }, [dispatch, user?.userId]);

  const refreshBalance = useCallback(() => {
    if (!user?.userId) return;
    dispatch(getAvailableBalance());
    dispatch(getMarginSummary());
  }, [dispatch, user?.userId]);

  const selectTab = useCallback(
    (id) => {
      setTab(id);
      if (id === "add" && user?.userId) dispatch(getRangeList());
    },
    [dispatch, user?.userId]
  );

  const handleWithdrawSuccess = useCallback(() => {
    onRefreshBalance?.();
    dispatch(getMarginSummary());
  }, [onRefreshBalance, dispatch]);

  return (
    <div
      className={`modal-overlay wallet-hub-overlay wallet-hub-overlay--fullpage platform-menu-modal-overlay ${themeClass}`}
      onClick={onClose}
    >
      <div
        className="modal-box wallet-hub-sheet wallet-hub-sheet--fullpage"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wallet-hub-title"
      >
        <div className="modal-header wallet-hub-header">
          <div className="wallet-hub-header__titles">
            <h2 id="wallet-hub-title">Account</h2>
            <p className="wallet-hub-sub">
              Balance, credit, equity, margin, deposits &amp; withdrawals
            </p>
          </div>
          <button type="button" className="modal-close wallet-hub-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="wallet-hub-balance-strip wallet-hub-balance-strip--margin">
          <AccountMarginBar
            summary={summary}
            loading={summaryLoading || availableBalanceLoading}
            onRefresh={refreshBalance}
            showWithdrawable
          />
          {!summary && (
            <div className="wallet-hub-balance-strip__main" style={{ marginTop: 8 }}>
              <span className="wallet-hub-balance-strip__label">Available balance</span>
              <span
                className="wallet-hub-balance-strip__value"
                style={{ color: ledgerColor || "var(--green)" }}
                title={ledgerLabel || undefined}
              >
                {availableBalanceLoading && !ledgerLabel ? (
                  <span className="wallet-pill__shimmer">···</span>
                ) : (
                  ledgerLabel ?? "—"
                )}
              </span>
            </div>
          )}
        </div>

        <nav className="wallet-hub-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={tab === t.id ? "active" : ""}
              onClick={() => selectTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div
          className={`wallet-hub-body${
            tab === "statement" || tab === "withdraw" || tab === "add"
              ? " wallet-hub-body--flow"
              : ""
          }${tab === "statement" ? " wallet-hub-body--statement" : ""}`}
        >
          {tab === "overview" && (
            <div className="wallet-hub-overview">
              <p className="wallet-hub-overview__text">
                Choose a quick action or switch tabs for the full flow.
              </p>
              <div className="wallet-hub-quick">
                <button
                  type="button"
                  className="wallet-hub-quick__btn wallet-hub-quick__btn--add"
                  onClick={() => selectTab("add")}
                >
                  <span className="wallet-hub-quick__icon" aria-hidden>+</span>
                  <span className="wallet-hub-quick__label">Add funds</span>
                  <span className="wallet-hub-quick__hint">Deposit to ledger</span>
                </button>
                <button
                  type="button"
                  className="wallet-hub-quick__btn wallet-hub-quick__btn--withdraw"
                  onClick={() => selectTab("withdraw")}
                >
                  <span className="wallet-hub-quick__icon" aria-hidden>↓</span>
                  <span className="wallet-hub-quick__label">Withdraw</span>
                  <span className="wallet-hub-quick__hint">Request payout</span>
                </button>
                <button
                  type="button"
                  className="wallet-hub-quick__btn wallet-hub-quick__btn--statement"
                  onClick={() => selectTab("statement")}
                >
                  <span className="wallet-hub-quick__icon wallet-hub-quick__icon--emoji" aria-hidden>📋</span>
                  <span className="wallet-hub-quick__label">Statement</span>
                  <span className="wallet-hub-quick__hint">View history</span>
                </button>
              </div>
            </div>
          )}

          {tab === "add" && (
            <div className="wallet-hub-panel wallet-hub-panel--add">
              <AddPointFlow
                embedded
                user={user}
                onClose={onClose}
                onSuccess={() => {
                  onAddSuccess?.();
                  dispatch(getAvailableBalance());
                  dispatch(getMarginSummary());
                }}
              />
            </div>
          )}

          {tab === "withdraw" && user?.userId && (
            <div className="wallet-hub-panel wallet-hub-panel--withdraw">
              <WithdrawPanel userId={user.userId} onWithdrawSuccess={handleWithdrawSuccess} darkTheme={darkTheme} />
            </div>
          )}

          {tab === "withdraw" && !user?.userId && (
            <p className="wallet-hub-muted">Sign in to request withdrawals.</p>
          )}

          {tab === "statement" && user?.userId && (
            <div className="wallet-hub-panel wallet-hub-panel--statement">
              <ViewStatementModal displayName={displayName} onClose={onClose} embedded />
            </div>
          )}

          {tab === "statement" && !user?.userId && (
            <p className="wallet-hub-muted">Sign in to view your statement.</p>
          )}
        </div>
      </div>
    </div>
  );
}
